<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Trips ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-row v-show="showSearch" align="center" style="max-width: 300px;">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search trip"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-btn class="ml-5" color="primary" dark :to="{ name: 'trips.create' }"
          >Create Trip</v-btn
        >
      </template>
    </v-app-bar>

    <div
      class="mt-12 mx-4"
      id="trip-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table>
        <thead>
          <tr>
            <th width="15%" colspan="2" class="text-left pa-0">Name</th>
            <th width="10%" class="text-left">Length of Trip</th>
            <th width="20%" class="text-left">Trip Type</th>
            <th width="20%" class="text-left">Accommodation</th>
            <th width="20%" class="text-left">Added Date</th>
            <th width="20%" class="text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="trip in trips" :key="trip.id">
            <td colspan="2">
              {{ trip.name }}
            </td>
            <td>
              {{ trip.length_of_trip.in_days }}
              {{ trip.length_of_trip.in_days > 1 ? 'Days' : 'Day' }}
            </td>
            <td>
              <v-chip
                class="ma-1"
                color="primary"
                v-for="type in trip.types"
                :key="type.id"
              >
                {{ type.name }}
              </v-chip>
            </td>
            <td>
              <v-chip
                class="ma-1"
                color="primary"
                v-for="accommodation in trip.accommodation_types"
                :key="accommodation.id"
              >
                {{ accommodation.name }}
              </v-chip>
            </td>
            <td>{{ trip.addedDate }}</td>
            <td>
              <div class="actions">
                <v-btn
                  color="primary"
                  fab
                  small
                  title="Edit"
                  @click="
                    $router.push({
                      name: 'trips.update',
                      params: { id: trip.id }
                    })
                  "
                >
                  <v-icon small>{{ icons.edit }}</v-icon>
                </v-btn>
                <v-btn
                  color="default"
                  fab
                  small
                  title="Copy"
                  @click="showCopyDialog(trip.id)"
                >
                  <v-icon small>{{ icons.copy }}</v-icon>
                </v-btn>
                <v-btn
                  color="error"
                  fab
                  small
                  @click="showDeleteDialog(trip.id)"
                  title="Delete"
                >
                  <v-icon small>{{ icons.delete }}</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>

          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="py-3 pl-0" width="5%">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
    </div>

    <ConfirmModal
      v-model="showDeleteModal"
      title="Delete Trip"
      :message="deleteMessage"
      @cancel="showDeleteModal = false"
      @confirm="deleteTripDetail"
    ></ConfirmModal>

    <ConfirmModal
      v-model="showCopyModal"
      title="Copy Trip"
      message="Are you sure you want to copy this trip?"
      @cancel="showCopyModal = false"
      @confirm="confirmCopyTrip()"
      :loading="copying"
    ></ConfirmModal>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiPencil,
  mdiTrashCanOutline,
  mdiContentCopy
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'
import ConfirmModal from '@/components/modals/ConfirmModal'

export default {
  name: 'TripList',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll
  },

  components: {
    AppBarNavIcon,
    ConfirmModal
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      showProfile: false,
      selectedProfile: null,
      filter: {
        search: ''
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        edit: mdiPencil,
        delete: mdiTrashCanOutline,
        copy: mdiContentCopy
      },
      showDeleteModal: false,
      deleteTripId: null,
      showCopyModal: false,
      copyTripId: null,
      copying: false
    }
  },

  created() {
    this.clearTrips()
    this.fetchTrips(1)
  },

  computed: {
    ...mapState({
      trips: state => state.trip.list,
      listMeta: state => state.trip.listMeta
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },

    deleteMessage() {
      return 'Are you sure you want to delete this record?'
    }
  },

  methods: {
    ...mapActions({
      getTrips: 'trip/getTrips',
      deleteTrip: 'trip/deleteTrip',
      copyTrip: 'trip/copyTrip',
      setAlert: 'alert/setAlert'
    }),

    ...mapMutations({
      clearTrips: 'trip/clearTripList'
    }),

    pageChanged(page) {
      this.fetchTrips(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchTrips(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearTrips()
      this.fetchTrips()
    }, 600),

    async fetchTrips(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      this.loading = true
      await this.getTrips(params)
      this.loading = false
    },

    async deleteTripDetail() {
      this.deleteTrip(this.deleteTripId)
        .then(() => {
          this.showDeleteModal = false
          this.deleteTripId = null
          this.clearTrips()
          this.fetchTrips(1)
          this.setAlert({
            message: 'Trip successfully deleted!',
            color: 'success'
          })
        })
        .catch(err => {
          this.setAlert({
            message: err.response.data.message,
            color: 'error'
          })
        })

      return true
    },

    async confirmCopyTrip() {
      this.copying = true
      this.copyTrip(this.copyTripId)
        .then(() => {
          this.showCopyModal = false
          this.copyTripId = null
          this.setAlert({
            message: 'Trip successfully copied!',
            color: 'success'
          })
        })
        .catch(err => {
          this.setAlert({
            message: err.response.data.message,
            color: 'error'
          })
        })
        .finally(() => {
          this.copying = false
        })
    },

    showDeleteDialog(tripId) {
      this.showDeleteModal = true
      this.deleteTripId = tripId
    },

    showCopyDialog(tripId) {
      this.showCopyModal = true
      this.copyTripId = tripId
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    }
  }
}
</script>

<style scoped>
.actions {
  display: flex;
  gap: 0.5rem;
}
</style>
